"use client";
import { Tag, Typography } from "antd";

const { Text } = Typography;

export default function SecurityMasterPreview({
  formData,
  productCateogory,
  instrumentType,
  assetClass,
  countryOfIssuer,
  issueTypeId,
  currency,
  dayCountConventionOptions,
  couponFrequencyOptions,
  businessDayConventionOptions,
  quotationTypeOptions,
  marketTypeOptions,
  valuationApproachOptions,
  productCodes,
}) {
  /* ---------------------------------- */
  /* HELPERS */
  /* ---------------------------------- */

  const resolveLabel = (options = [], value) => {
    if (value === null || value === undefined) return "-";
    const found = options.find((o) => o.value === value);
    return found?.label ?? "-";
  };

  const formatFullDate = (date) => {
    if (!date) return "-";
    try {
      return new Date(date).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const renderRow = (name, value, index, total) => {
    const isLast = index === total - 1;

    return (
      <div
        key={name}
        style={{
          display: "flex",
          borderBottom: isLast ? "none" : "1px solid #f0f0f0",
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            width: "50%",
            backgroundColor: "#f9f9f9",
            fontWeight: 500,
            borderRight: "1px solid #f0f0f0",
          }}
        >
          {name}
        </div>

        <div style={{ padding: "16px 24px", width: "50%" }}>
          <Text style={{ fontWeight: 600 }}>{value || "-"}</Text>
        </div>
      </div>
    );
  };

  /* ---------------------------------- */
  /* SECTIONS */
  /* ---------------------------------- */

  const sections = [
    {
      title: "1. Instrument Identification",
      items: [
        {
          name: "Security Name",
          value: formData.instrumentIdentification?.instrumentName,
        },
        { name: "ISIN", value: formData.instrumentIdentification?.isin },
        // { name: "CUSIP", value: formData.instrumentIdentification?.cusip },
        {
          name: "Local Code",
          value: formData.instrumentIdentification?.localCode,
        },
      ],
    },

    {
      title: "2. Classification Fields",
      items: [
        {
          name: "Product Category",
          value: resolveLabel(
            productCateogory,
            formData.classificationFields?.instrumentCategoryId,
          ),
        },
        {
          name: "Instrument Type",
          value: resolveLabel(
            instrumentType,
            formData.classificationFields?.instrumentTypeId,
          ),
        },
        {
          name: "Asset Class",
          value: resolveLabel(
            assetClass,
            formData.classificationFields?.assetClassId,
          ),
        },
        {
          name: "Issuer Type",
          value: resolveLabel(
            issueTypeId,
            formData.classificationFields?.issueTypeId,
          ),
        },
        {
          name: "Issuer Name",
          value: formData.classificationFields?.issueName,
        },
        {
          name: "Country of Issuer",
          value: resolveLabel(
            countryOfIssuer,
            formData.classificationFields?.countryId,
          ),
        },
      ],
    },

    {
      title: "3. Instrument Structure / Terms",
      items: [
        {
          name: "Currency",
          value: resolveLabel(
            currency,
            formData.instrumentStructure?.currencyCode,
          ),
        },
        {
          name: "Face Value",
          value: formData.instrumentStructure?.faceValueDenomination
            ? new Intl.NumberFormat("en-US").format(
                formData.instrumentStructure.faceValueDenomination,
              )
            : "-",
        },
        {
          name: "Issue Date",
          value: formatFullDate(formData.instrumentStructure?.issueDate),
        },
        {
          name: "Maturity Date",
          value: formatFullDate(formData.instrumentStructure?.maturityDate),
        },
        {
          name: "Day Count Convention",
          value: resolveLabel(
            dayCountConventionOptions,
            formData.instrumentStructure?.dayCountConventionId,
          ),
        },
        {
          name: "Business Day Convention",
          value: resolveLabel(
            businessDayConventionOptions,
            formData.instrumentStructure?.businessDayConventionId,
          ),
        },
        {
          name: "Coupon Rate (%)",
          value: formData.instrumentStructure?.couponRate,
        },
        {
          name: "Coupon Frequency",
          value: resolveLabel(
            couponFrequencyOptions,
            formData.instrumentStructure?.couponFrequencyId,
          ),
        },
        {
          name: "Coupon Basis",
          value: formData.instrumentStructure?.couponBasis,
        },
        {
          name: "Reset Index",
          value: formData.instrumentStructure?.resetIndex,
        },
      ],
    },

    {
      title: "4. Pricing Information",
      items: [
        {
          name: "Linked Product Code",
          value: resolveLabel(
            productCodes,
            formData.pricingInformation?.linkedProductId,
          ),
        },
        {
          name: "Quotation Type",
          value: resolveLabel(
            quotationTypeOptions,
            formData.pricingInformation?.quotationTypeId,
          ),
        },
        {
          name: "Market Type",
          value: resolveLabel(
            marketTypeOptions,
            formData.pricingInformation?.marketTypeId,
          ),
        },
        {
          name: "Decimal Place",
          value: formData.pricingInformation?.ticketSize,
        },
        {
          name: "Valuation Approach",
          value: resolveLabel(
            valuationApproachOptions,
            formData.pricingInformation?.valuationApproachId,
          ),
        },
      ],
    },
  ];

  return (
    <div style={{ width: "100%", padding: "0 24px" }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: "24px 0 8px" }}>
        Preview
      </h1>

      <p style={{ color: "#6b7280", marginBottom: 24 }}>
        Review the security master instrument details before submission
      </p>

      {sections.map((section, i) => (
        <div key={i}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "24px 0 16px" }}>
            {section.title}
          </h2>

          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              overflow: "hidden",
              marginBottom: 24,
            }}
          >
            {section.items.map((item, idx) =>
              renderRow(item.name, item.value, idx, section.items.length),
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
